import axios from 'axios';
import toast from 'react-hot-toast';
import { store } from '../application/store';
import { logout } from '../application/reducers/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}api/`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Common toast configuration options
const toastOptions = {
  position: 'top-right',
  style: {
    fontSize: '14px',
  },
};

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const state = store.getState();
  const token = state.auth?.token;
  const encryptedMenus = state.auth?.encryptedMenus;
  const academicId = state.auth?.current_academic_id;
  const instituteId = state.auth?.user?.institute_id;
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    
    // Only add encrypted menu header if it exists, is not null/undefined, and is a valid string
    if (encryptedMenus && typeof encryptedMenus === 'string' && encryptedMenus.trim() !== '' && encryptedMenus !== 'null' && encryptedMenus !== 'undefined') {
      config.headers['X-Encrypted-Menu'] = encryptedMenus;
    }
    
    // Add request pathname for debugging/tracking
    if (typeof window !== 'undefined' && window.location && window.location.pathname) {
      config.headers['X-Request-Pathname'] = window.location.pathname;
    }
  }
  
  // Add academic_id and institute_id as query parameters to all requests
  if (!config.params) {
    config.params = {};
  }
  if (academicId) {
    config.params.academic_id = academicId;
    config.headers['X-Academic-Id'] = academicId;
  }
  
  if (instituteId) {
    config.params.institute_id = instituteId;
    config.headers['X-Institute-Id'] = instituteId;
  }

  // Let axios set the correct Content-Type (including boundary) for multipart uploads
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// Track if apiCallWithToast is currently handling errors
let isApiCallWithToastActive = false;

// Response interceptor to handle errors and show toasts
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't show toast if this is being handled by apiCallWithToast
    // We can detect this by checking if the request config has a special flag or if apiCallWithToast is active
    if ((error.config && error.config._skipInterceptorToast) || isApiCallWithToastActive) {
      return Promise.reject(error);
    }
    
    let errorMessage = 'An error occurred';
    
    if (error?.message) {
      // Error occurred during request setup
      errorMessage = error.message;
    } else if (error.response) {
      // Server responded with a status code outside of the 2xx range
      const { data, status } = error.response;
      if (data) {
        // Check for custom response with success: false and message
        if (data.success === false && data.message) {
          errorMessage = data.message;
        }
        // Laravel validation errors (422 status)
        else if (status === 422 && data.errors) {
          // Extract first validation error
          const firstError = Object.values(data.errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
        // Laravel general errors
        else if (data.message) {
          errorMessage = data.message;
        }
        // Laravel exception errors
        else if (data.error) {
          errorMessage = data.error;
        }
        // Authentication errors
        else if (status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        }
        // Authorization errors
        else if (status === 403) {
          errorMessage = 'You are not authorized to perform this action.';
        }
        // Not found errors
        else if (status === 404) {
          errorMessage = 'The requested resource was not found.';
        }
        // Server errors
        else if (status >= 500) {
          errorMessage = 'Server error occurred. Please try again later.';
        }
        // Fallback to status text or generic message
        else {
          errorMessage = error.response.statusText || 'An error occurred';
        }
      }
    } else if (error.request) {
      // Request was made but no response was received
      errorMessage = 'No response received from server. Please check your connection.';
    }
    
    // Display the error message


    if(error.response && error.response.status === 401) {
      toast.error(errorMessage, toastOptions);
      // Dispatch logout and navigate to home root
      store.dispatch(logout());
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Export the configured axios client for use in other service files
export { apiClient };

// Wrapper function for using toast.promise with all API calls
export const apiCallWithToast = async (apiCall, customMessages = {}) => {
  toast.remove(); // Clear any existing toasts
  
  const defaultMessages = {
    loading: 'Processing...',
    success: 'Operation successful!',
    error: 'An error occurred during the operation.',
  };
  
  const messages = { ...defaultMessages, ...customMessages };
  
  // Set the flag to prevent interceptor from showing toasts
  isApiCallWithToastActive = true;
  
  try {
    const result = await toast.promise(
      apiCall,
      {
        loading: messages.loading,
        success: (data) => {
          // Check if the response contains a success message
          if (data?.data?.message) {
            return data.data.message;
          }
          return messages.success;
        },
        error: (err) => {
          // Extract error message similar to interceptor logic
          let errorMessage = messages.error;
          
          if (err?.response?.data) {
            const { data, status } = err.response;
            
            // Check for custom response with success: false and message
            if (data.success === false && data.message) {
              errorMessage = data.message;
            }
            // Laravel validation errors (422 status)
            else if (status === 422 && data.errors) {
              const firstError = Object.values(data.errors)[0];
              errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
            } 
            // Laravel general errors
            else if (data.message) {
              errorMessage = data.message;
            } 
            // Laravel exception errors
            else if (data.error) {
              errorMessage = data.error;
            }
          }
          
          return errorMessage;
        }
      },
      toastOptions
    );
    
    return result;
  } finally {
    // Reset the flag
    isApiCallWithToastActive = false;
  }
};

// Classes API
export const classesAPI = {
  // Get all classes with filters
  getAll: (params = {}) => apiClient.get('classes', { params }),
  
  // Get class analytics
  getAnalytics: (params = {}) => apiClient.get('classes-analytics', { params }),
  
  // Get single class
  getById: (id) => apiClient.get(`classes/${id}`),
  
  // Create new class
  create: (data) => apiClient.post('classes', data),
  
  // Update class
  update: (id, data) => apiClient.put(`classes/${id}`, data),
  
  // Delete class
  delete: (id) => apiClient.delete(`classes/${id}`),
  
  // Get sections for a class
  getSections: (id) => apiClient.get(`classes/${id}/sections`),

  // Get only class list
  getOnlyClassList: (params = {}) => apiClient.get('class-list', { params }),

  // Download bulk import template
  downloadTemplate: (params = {}) => apiClient.get('classes/bulk-import/template', { params, responseType: 'blob' }),

  // Bulk import classes
  bulkImport: (formData) => apiClient.post('classes/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Sections API
export const sectionsAPI = {
  // Get all sections with filters
  getAll: (params = {}) => apiClient.get('sections', { params }),
  
  // Get section analytics
  getAnalytics: (params = {}) => apiClient.get('sections-analytics', { params }),
  
  // Get single section
  getById: (id) => apiClient.get(`sections/${id}`),
  
  // Create new section
  create: (data) => apiClient.post('sections', data),
  
  // Update section
  update: (id, data) => apiClient.put(`sections/${id}`, data),
  
  // Delete section
  delete: (id) => apiClient.delete(`sections/${id}`),
  
  // Get students in section
  getStudents: (id) => apiClient.get(`sections/${id}/students`),
  
  // Transfer students
  transferStudents: (id, data) => apiClient.post(`sections/${id}/transfer-students`, data),

  // Get subjects for a section
  getSubjects: (id) => apiClient.get(`sections/${id}/subjects`),

  // Download bulk import template
  downloadTemplate: (params = {}) => apiClient.get('sections/bulk-import/template', { params, responseType: 'blob' }),

  // Bulk import sections
  bulkImport: (formData) => apiClient.post('sections/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Timetable API
export const timetableAPI = {
  // Get weekly timetable for section
  getWeeklyTimetable: (sectionId) => apiClient.get(`session-timetable/section/${sectionId}/weekly`),
  
  // Get available teachers for section
  getAvailableTeachers: (sectionId) => apiClient.get(`session-timetable/section/${sectionId}/teachers`),
  
  // Create new timetable session
  create: (data) => apiClient.post('session-timetable', data),
  
  // Update timetable session
  update: (id, data) => apiClient.put(`session-timetable/${id}`, data),
  
  // Delete timetable session
  delete: (id) => apiClient.delete(`session-timetable/${id}`),
  
  // Force delete time slot - removes all sessions for specific time slot
  forceDeleteTimeslot: (data) => apiClient.delete('session-timetable/timeslot/force-delete', { data }),
  
  // Bulk operations
  bulkUpdate: (data) => apiClient.post('session-timetable/bulk-update', data),
  
  // Student and teacher specific endpoints
  getStudentTimetable: (params = {}) => apiClient.get('session-timetable/student/timetable', { params }),
  getTeacherWeeklyTimetable: (params = {}) => apiClient.get('session-timetable/teacher/weekly', { params }),
  
  // Legacy endpoints for backward compatibility
  getTeacherTimetable: (teacherId, params = {}) => apiClient.get(`timetable-helpers/teacher/${teacherId}/availability`, { params }),
  getClassTimetable: (classId, params = {}) => apiClient.get(`session-timetable/section/${classId}/weekly`, { params }),
};

// Subjects API
export const subjectsAPI = {
  // Get all subjects with filters
  getAll: (params = {}) => apiClient.get('subjects', { params }),
  
  // Get subject analytics
  getAnalytics: (params = {}) => apiClient.get('subjects-analytics', { params }),
  
  // Get single subject
  getById: (id) => apiClient.get(`subjects/${id}`),
  
  // Get subject with all section assignments (grouped by subject_code)
  getSubjectWithSections: (params = {}) => apiClient.get('subjects/with-sections', { params }),
  
  // Create new subject
  create: (data) => apiClient.post('subjects', data),
  
  // Update subject
  update: (id, data) => apiClient.put(`subjects/${id}`, data),
  
  // Delete subject
  delete: (id) => apiClient.delete(`subjects/${id}`),
  
  // Get subjects by class
  getByClass: (classId) => apiClient.get(`subjects/class/${classId}`),
  
  // Get subjects by section
  getBySection: (sectionId) => apiClient.get(`subjects/section/${sectionId}`),
  
  // Bulk create subjects
  bulkCreate: (data) => apiClient.post('subjects/bulk-create', data),
  
  // Download bulk import template
  downloadTemplate: (params = {}) => {
    return apiClient.get('subjects/bulk-import/template', {
      responseType: 'blob',
      params
    });
  },
  
  // Export subjects
  exportSubjects: (params = {}) => {
    return apiClient.get('subjects/export', {
      responseType: 'blob',
      params
    });
  },
  
  // Bulk import subjects
  bulkImport: (formData) => {
    return apiClient.post('subjects/bulk-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// Roles API
export const rolesAPI = {
  // Get all roles
  getRoles: () => apiClient.get('roles'),
  
  // Get single role
  getRole: (id) => apiClient.get(`roles/${id}`),
  
  // Create new role
  createRole: (data) => apiClient.post('roles', data),
  
  // Update role
  updateRole: (id, data) => apiClient.put(`roles/${id}`, data),
  
  // Delete role
  deleteRole: (id) => apiClient.delete(`roles/${id}`),
  
  // Get role permissions
  getRolePermissions: (id) => apiClient.get(`roles/${id}/permissions`),
  
  // Update role permissions
  updateRolePermissions: (id, data) => apiClient.post(`roles/${id}/permissions`, data),
  
  // Get available menus for role assignment
  getAvailableMenus: () => apiClient.get('roles/menus/available'),
  
  // Assign role to user
  assignRoleToUser: (data) => apiClient.post('roles/assign-user', data)
};

// Menu API for menu management
export const menuAPI = {
  // Main Menu operations
  getMainMenus: (params = {}) => apiClient.get('main-menus', { params }),
  getMainMenu: (id) => apiClient.get(`main-menus/${id}`),
  createMainMenu: (data) => apiClient.post('main-menus', data),
  updateMainMenu: (id, data) => apiClient.put(`main-menus/${id}`, data),
  deleteMainMenu: (id) => apiClient.delete(`main-menus/${id}`),

  // Sub Menu operations
  getSubMenus: (params = {}) => apiClient.get('sub-menus', { params }),
  getSubMenu: (id) => apiClient.get(`sub-menus/${id}`),
  getSubMenusByMainMenu: (mainMenuId) => apiClient.get(`main-menus/${mainMenuId}/sub-menus`),
  createSubMenu: (data) => apiClient.post('sub-menus', data),
  updateSubMenu: (id, data) => apiClient.put(`sub-menus/${id}`, data),
  deleteSubMenu: (id) => apiClient.delete(`sub-menus/${id}`),

  // Main Menu Features operations
  getMainMenuFeatures: (params = {}) => apiClient.get('main-menu-features', { params }),
  getMainMenuFeature: (id) => apiClient.get(`main-menu-features/${id}`),
  getMainMenuFeaturesByMainMenu: (mainMenuId) => apiClient.get(`main-menus/${mainMenuId}/features`),
  createMainMenuFeature: (data) => apiClient.post('main-menu-features', data),
  updateMainMenuFeature: (id, data) => apiClient.put(`main-menu-features/${id}`, data),
  deleteMainMenuFeature: (id) => apiClient.delete(`main-menu-features/${id}`),

  // Sub Menu Features operations
  getSubMenuFeatures: (params = {}) => apiClient.get('sub-menu-features', { params }),
  getSubMenuFeature: (id) => apiClient.get(`sub-menu-features/${id}`),
  getSubMenuFeaturesBySubMenu: (subMenuId) => apiClient.get(`sub-menus/${subMenuId}/features`),
  createSubMenuFeature: (data) => apiClient.post('sub-menu-features', data),
  updateSubMenuFeature: (id, data) => apiClient.put(`sub-menu-features/${id}`, data),
  deleteSubMenuFeature: (id) => apiClient.delete(`sub-menu-features/${id}`),

  // Hierarchy operations
  getMenuHierarchy: () => apiClient.get('menu-hierarchy'),
  reorderMenus: (data) => apiClient.post('menu-hierarchy/reorder', data),
  
  // Bulk operations
  bulkCreateMenus: (data) => apiClient.post('menus/bulk-create', data),
  bulkUpdateMenus: (data) => apiClient.put('menus/bulk-update', data),
  bulkDeleteMenus: (ids) => apiClient.delete('menus/bulk-delete', { data: { ids } }),

  // Menu validation
  validateMenuStructure: (data) => apiClient.post('menu-hierarchy/validate', data),
  
  // Menu export/import
  exportMenus: (format = 'json') => apiClient.get(`menus/export?format=${format}`),
  importMenus: (data) => apiClient.post('menus/import', data)
};

// Menu helper functions for menu operations
export const menuHelpers = {
  // Build menu hierarchy from flat arrays
  buildHierarchy: (mainMenus, subMenus, mainMenuFeatures, subMenuFeatures) => {
    return mainMenus.map(mainMenu => ({
      ...mainMenu,
      subMenus: subMenus.filter(subMenu => subMenu.main_menu_id === mainMenu.main_menu_id),
      features: mainMenuFeatures.filter(feature => feature.main_menu_id === mainMenu.main_menu_id)
    })).map(mainMenu => ({
      ...mainMenu,
      subMenus: mainMenu.subMenus.map(subMenu => ({
        ...subMenu,
        features: subMenuFeatures.filter(feature => feature.sub_menu_id === subMenu.sub_menu_id)
      }))
    }));
  },

  // Validate menu data before submission
  validateMenuData: (menuData, type) => {
    const errors = [];
    
    if (!menuData.name || menuData.name.trim() === '') {
      errors.push('Menu name is required');
    }
    
    if (!menuData.url || menuData.url.trim() === '') {
      errors.push('Menu URL is required');
    }
    
    if (type === 'subMenu' && !menuData.main_menu_id) {
      errors.push('Main menu selection is required for sub menu');
    }
    
    if (type === 'mainMenuFeature' && !menuData.main_menu_id) {
      errors.push('Main menu selection is required for main menu feature');
    }
    
    if (type === 'subMenuFeature' && !menuData.sub_menu_id) {
      errors.push('Sub menu selection is required for sub menu feature');
    }
    
    return errors;
  },

  // Format menu data for API calls
  formatMenuData: (menuData, type) => {
    const formatted = { ...menuData };
    
    // Convert string IDs to numbers if needed
    if (formatted.main_menu_id) {
      formatted.main_menu_id = parseInt(formatted.main_menu_id);
    }
    
    if (formatted.sub_menu_id) {
      formatted.sub_menu_id = parseInt(formatted.sub_menu_id);
    }
    
    // Ensure required fields are present
    if (!formatted.name) formatted.name = '';
    if (!formatted.url) formatted.url = '';
    
    // Handle optional fields
    if (type === 'mainMenu' && !formatted.icon) {
      formatted.icon = null;
    }
    
    if ((type === 'mainMenuFeature' || type === 'subMenuFeature') && !formatted.description) {
      formatted.description = null;
    }
    
    return formatted;
  }
};

// Students API
export const studentsAPI = {
  // Get all students with filters
  getAll: (params = {}) => apiClient.get('students', { params }),
  
  // Get student analytics/dashboard data
  getAnalytics: (params = {}) => apiClient.get('students-analytics', { params }),
  
  // Get single student with complete information
  getById: (id) => apiClient.get(`students/${id}`),
  
  // Create new student
  create: (data) => apiClient.post('students', data),
  
  // Update student
  update: (id, data) => apiClient.put(`students/${id}`, data),
  
  // Delete student
  delete: (id) => apiClient.delete(`students/${id}`),
  
  // Get students by class and section
  getByClassSection: (params = {}) => apiClient.get('students/by-class-section', { params }),
  
  // Get students by class
  getByClass: (classId, params = {}) => apiClient.get(`students/by-class/${classId}`, { params }),
  
  // Transfer students between classes/sections
  transferStudents: (data) => apiClient.post('students/transfer', data),
  
  // Get student's parent information and address details
  getParentDetails: (studentId) => apiClient.get(`students/${studentId}/parent-details`),
  
  // Bulk import students from Excel
  bulkImport: (formData) => {
    return apiClient.post('students/bulk-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Download Excel template for bulk import
  downloadTemplate: () => {
    return apiClient.get('students/download-template', {
      responseType: 'blob'
    });
  },
  
  // Export students data with current filters
  exportStudents: (params = {}) => {
    return apiClient.get('students/export', {
      params,
      responseType: 'blob'
    });
  },
  
  // Student attendance summary
  getAttendance: (studentId, params = {}) => apiClient.get(`students/${studentId}/attendance`, { params }),
  
  // Student fees summary
  getFees: (studentId, params = {}) => apiClient.get(`students/${studentId}/fees`, { params }),
  
  // Student academic progress
  getAcademicProgress: (studentId, params = {}) => apiClient.get(`students/${studentId}/academic-progress`, { params }),

  // ---- Student Promotion ----

  // Get promotion preview (source/target classes with student counts)
  getPromotionPreview: (params) => apiClient.get('student-promotion/preview', { params }),

  // Get auto-suggested class mapping between academic years
  getClassMapping: (params) => apiClient.get('student-promotion/class-mapping', { params }),

  // Get students by class-section for promotion selection
  getPromotionStudents: (params) => apiClient.get('student-promotion/students', { params }),

  // Execute student promotion
  promoteStudents: (data) => apiClient.post('student-promotion/promote', data),

  // Get promotion history
  getPromotionHistory: (params = {}) => apiClient.get('student-promotion/history', { params }),

  // Rollback a promotion batch
  rollbackPromotion: (data) => apiClient.post('student-promotion/rollback', data),
};

// Student helper functions for student operations
export const studentHelpers = {
  // Validate student form data before submission
  validateStudentData: (studentData, isUpdate = false) => {
    const errors = [];
    
    if (!studentData.first_name?.trim()) {
      errors.push('First name is required');
    }
    
    if (!studentData.last_name?.trim()) {
      errors.push('Last name is required');
    }
    
    if (!studentData.email?.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(studentData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!isUpdate && !studentData.password?.trim()) {
      errors.push('Password is required');
    }
    
    if (!studentData.phone?.trim()) {
      errors.push('Phone number is required');
    }
    
    if (!studentData.gender) {
      errors.push('Gender is required');
    }
    
    if (!studentData.date_of_birth) {
      errors.push('Date of birth is required');
    }
    
    if (!studentData.class_id) {
      errors.push('Class selection is required');
    }
    
    if (!studentData.section_id) {
      errors.push('Section selection is required');
    }
    
    if (!studentData.address_line_1?.trim()) {
      errors.push('Address is required');
    }
    
    if (!studentData.city?.trim()) {
      errors.push('City is required');
    }
    
    if (!studentData.state?.trim()) {
      errors.push('State is required');
    }
    
    if (!studentData.postal_code?.trim()) {
      errors.push('Postal code is required');
    }
    
    if (!studentData.parent_name?.trim()) {
      errors.push('Parent name is required');
    }
    
    if (!studentData.parent_phone?.trim()) {
      errors.push('Parent phone is required');
    }
    
    if (!studentData.parent_relation?.trim()) {
      errors.push('Parent relation is required');
    }
    
    if (!studentData.enrollment_date) {
      errors.push('Enrollment date is required');
    }
    
    if (!studentData.tuition_fee || studentData.tuition_fee < 0) {
      errors.push('Valid tuition fee is required');
    }
    
    if (!studentData.total_fee || studentData.total_fee < 0) {
      errors.push('Valid total fee is required');
    }
    
    return errors;
  },

  // Format student data for API calls
  formatStudentData: (studentData) => {
    const formatted = { ...studentData };
    
    // Convert string numbers to actual numbers
    if (formatted.class_id) formatted.class_id = parseInt(formatted.class_id);
    if (formatted.section_id) formatted.section_id = parseInt(formatted.section_id);
    if (formatted.tuition_fee) formatted.tuition_fee = parseFloat(formatted.tuition_fee);
    if (formatted.total_fee) formatted.total_fee = parseFloat(formatted.total_fee);
    if (formatted.exam_fee) formatted.exam_fee = parseFloat(formatted.exam_fee) || 0;
    if (formatted.library_fee) formatted.library_fee = parseFloat(formatted.library_fee) || 0;
    if (formatted.lab_fee) formatted.lab_fee = parseFloat(formatted.lab_fee) || 0;
    if (formatted.sports_fee) formatted.sports_fee = parseFloat(formatted.sports_fee) || 0;
    if (formatted.miscellaneous_fee) formatted.miscellaneous_fee = parseFloat(formatted.miscellaneous_fee) || 0;
    if (formatted.fee_discount) formatted.fee_discount = parseFloat(formatted.fee_discount) || 0;
    if (formatted.transport_fee) formatted.transport_fee = parseFloat(formatted.transport_fee) || 0;
    if (formatted.transport_discount) formatted.transport_discount = parseFloat(formatted.transport_discount) || 0;
    if (formatted.hostel_fee) formatted.hostel_fee = parseFloat(formatted.hostel_fee) || 0;
    if (formatted.hostel_discount) formatted.hostel_discount = parseFloat(formatted.hostel_discount) || 0;
    if (formatted.scholarship_amount) formatted.scholarship_amount = parseFloat(formatted.scholarship_amount) || 0;
    
    // Convert boolean strings to actual booleans
    if (typeof formatted.transport_required === 'string') {
      formatted.transport_required = formatted.transport_required === 'true';
    }
    if (typeof formatted.hostel_required === 'string') {
      formatted.hostel_required = formatted.hostel_required === 'true';
    }
    if (typeof formatted.scholarship_applicable === 'string') {
      formatted.scholarship_applicable = formatted.scholarship_applicable === 'true';
    }
    
    // Clean up empty strings to null for optional fields
    const optionalFields = [
      'roll_number', 'admission_number', 'student_id', 'aadhar_card_no',
      'birth_place', 'birth_state', 'birth_country', 'blood_group', 
      'religion', 'category', 'address_line_2', 'country',
      'parent_email', 'parent_occupation', 'transport_route', 
      'pickup_point', 'drop_point', 'hostel_name', 'room_number', 
      'bed_number', 'house_name',
      'scholarship_name', 'scholarship_type'
    ];
    
    optionalFields.forEach(field => {
      if (formatted[field] === '') {
        formatted[field] = null;
      }
    });
    
    return formatted;
  },

  // Get student full name
  getFullName: (student) => {
    return `${student.first_name || ''} ${student.last_name || ''}`.trim();
  },

  // Get student display name with roll number
  getDisplayName: (student) => {
    const fullName = studentHelpers.getFullName(student);
    const rollNumber = student.additional_info?.roll_number || student.academic_info?.[0]?.roll_number;
    return rollNumber ? `${fullName} (${rollNumber})` : fullName;
  },

  // Calculate student age
  getAge: (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  },

  // Get student status badge info
  getStatusBadge: (student) => {
    const isActive = student?.class_section_by_academic?.status;
    return {
      variant: isActive ? 'success' : 'danger',
      text: isActive ? 'Active' : 'Inactive'
    };
  },

  // Format student data for display in tables/cards
  formatForDisplay: (student) => {
    return {
      ...student,
      full_name: studentHelpers.getFullName(student),
      display_name: studentHelpers.getDisplayName(student),
      age: studentHelpers.getAge(student.additional_info?.date_of_birth),
      status_badge: studentHelpers.getStatusBadge(student),
      class_name: student.academic_info?.[0]?.student_class?.class_name || 'N/A',
      section_name: student.academic_info?.[0]?.section?.section_name || 'N/A',
      roll_number: student.academic_info?.[0]?.roll_number || 'N/A',
      admission_number: student.academic_info?.[0]?.admission_number || 'N/A'
    };
  }
};

// Import attendance API
export { attendanceAPI } from './attendanceAPI';

// Import holiday API
export { holidayAPI } from './holidayAPI';

// Import teacher API
export { teacherAPI } from './teacherAPI';

// Import employee API
export { default as employeeAPI } from './employeeAPI';

// Import dashboard API
export { dashboardAPI, dashboardHelpers } from './dashboardAPI';

// Assets API for authenticated image requests
export const assetsAPI = {
  getImage: (folder, filename, size = '200x200') => 
    apiClient.get(`assets/image/${folder}/${filename}/${size}`, {
      responseType: 'blob',
      headers: { 'Accept': 'image/*' }
    }),
  getFile: (path) => 
    apiClient.get(`assets/file/${path}`, {
      responseType: 'blob'
    })
};

// Fee Management APIs
export const feeCategoriesAPI = {
  // Get all fee categories with filters
  getAll: (params = {}) => apiClient.get('fee-categories', { params }),
  
  // Get active fee categories
  getActive: (params = {}) => apiClient.get('fee-categories/active', { params }),
  
  // Get single fee category
  getById: (id) => apiClient.get(`fee-categories/${id}`),
  
  // Create new fee category
  create: (data) => apiClient.post('fee-categories', data),
  
  // Update fee category
  update: (id, data) => apiClient.put(`fee-categories/${id}`, data),
  
  // Delete fee category
  delete: (id) => apiClient.delete(`fee-categories/${id}`)
};

export const feeStructuresAPI = {
  // Get all fee structures with filters
  getAll: (params = {}) => apiClient.get('fee-structures', { params }),
  
  // Get single fee structure
  getById: (id) => apiClient.get(`fee-structures/${id}`),
  
  // Create new fee structure
  create: (data) => apiClient.post('fee-structures', data),
  
  // Update fee structure
  update: (id, data) => apiClient.put(`fee-structures/${id}`, data),
  
  // Delete fee structure
  delete: (id) => apiClient.delete(`fee-structures/${id}`),
  
  // Apply fee structure to students
  applyToStudents: (id, data) => apiClient.post(`fee-structures/${id}/apply-to-students`, data),
  
  // Get students for applying fee structure
  getStudentsForApply: (id) => apiClient.get(`fee-structures/${id}/students-for-apply`)
};

export const studentFeesAPI = {
  // Get all student fees with filters
  getAll: (params = {}) => apiClient.get('student-fees', { params }),
  
  // Get analytics summary
  getAnalytics: (params = {}) => apiClient.get('student-fees/analytics', { params }),
  
  // Get single student fee
  getById: (id) => apiClient.get(`student-fees/${id}`),
  
  // Create new student fee
  create: (data) => apiClient.post('student-fees', data),
  
  // Update student fee
  update: (id, data) => apiClient.put(`student-fees/${id}`, data),
  
  // Delete student fee
  delete: (id) => apiClient.delete(`student-fees/${id}`),
  
  // Get fees for specific student
  getStudentFees: (studentId, params = {}) => apiClient.get(`student-fees/student/${studentId}`, { params }),
  
  // Waive student fee
  waiveFee: (id, data) => apiClient.post(`student-fees/${id}/waive`, data),
  
  // Export fees to Excel
  exportFees: (params = {}) => {
    return apiClient.get('student-fees/export', {
      params,
      responseType: 'blob'
    });
  },
  
  // Import fees from Excel
  importFees: (formData) => {
    return apiClient.post('student-fees/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Download sample import file
  downloadSampleImport: () => {
    return apiClient.get('student-fees/sample-import', {
      responseType: 'blob'
    });
  }
};

export const feePaymentsAPI = {
  // Get all fee payments with filters
  getAll: (params = {}) => apiClient.get('fee-payments', { params }),
  
  // Get payment statistics
  getStats: (params = {}) => apiClient.get('fee-payments/stats', { params }),
  
  // Get single fee payment
  getById: (id) => apiClient.get(`fee-payments/${id}`),
  
  // Create new fee payment
  create: (data) => apiClient.post('fee-payments', data),
  
  // Update fee payment
  update: (id, data) => apiClient.put(`fee-payments/${id}`, data),
  
  // Delete fee payment
  delete: (id) => apiClient.delete(`fee-payments/${id}`),
  
  // Verify payment
  verifyPayment: (id, data) => apiClient.post(`fee-payments/${id}/verify`, data),
  
  // Refund payment
  refundPayment: (id, data) => apiClient.post(`fee-payments/${id}/refund`, data),
  
  // Download payment receipt
  downloadReceipt: (id) => {
    return apiClient.get(`fee-payments/${id}/receipt`, {
      responseType: 'blob'
    });
  }
};

export const feeAnalyticsAPI = {
  // Get dashboard analytics
  getDashboard: (params = {}) => apiClient.get('fee-analytics/dashboard', { params }),
  
  // Get overview analytics (uses dashboard endpoint)
  getOverview: (params = {}) => apiClient.get('fee-analytics/dashboard', { params }),
  
  // Get trends analytics (uses dashboard endpoint)
  getTrends: (params = {}) => apiClient.get('fee-analytics/dashboard', { params }),
  
  // Get defaulters analytics (uses dashboard endpoint)
  getDefaulters: (params = {}) => apiClient.get('fee-analytics/dashboard', { params }),

  // Get paginated overdue fees with full student details
  getOverdueFees: (params = {}) => apiClient.get('fee-analytics/overdue-fees', { params }),
  
  // Export analytics
  exportAnalytics: (params = {}) => {
    return apiClient.get('fee-analytics/export', {
      params,
      responseType: 'blob'
    });
  }
};

// Student Groups API
export const studentGroupsAPI = {
  // Get all student groups with filters
  getAll: (params = {}) => apiClient.get('student-groups', { params }),
  
  //
  getAllForFeeModule: (params = {}) => apiClient.get('fee-student-groups', { params }),

  // Get single student group
  getById: (id) => apiClient.get(`student-groups/${id}`),
  
  // Create new student group
  create: (data) => apiClient.post('student-groups', data),
  
  // Update student group
  update: (id, data) => apiClient.put(`student-groups/${id}`, data),
  
  // Delete student group
  delete: (id) => apiClient.delete(`student-groups/${id}`),
  
  // Get available students for group selection
  getAvailableStudents: (params) => apiClient.get(`student-groups/available-students`, { params }),
  
  // Get group members
  getMembers: (groupId) => apiClient.get(`student-groups/${groupId}/members`),
  
  // Add members to group
  addMembers: (groupId, data) => apiClient.post(`student-groups/${groupId}/members`, data),
  
  // Remove member from group
  removeMember: (groupId, memberId) => apiClient.delete(`student-groups/${groupId}/members/${memberId}`),
};

// Student Group helper functions
export const studentGroupHelpers = {
  // Validate student group data
  validateGroupData: (data) => {
    const errors = [];
    
    if (!data.group_name?.trim()) {
      errors.push('Group name is required');
    }
    
    if (data.group_name && data.group_name.length > 100) {
      errors.push('Group name must be less than 100 characters');
    }
    
    if (!['active', 'inactive', 'archived'].includes(data.status)) {
      errors.push('Invalid status');
    }
    
    return errors;
  },

  // Format group data for display
  formatForDisplay: (group) => {
    return {
      ...group,
      member_count: group.members?.length || 0,
      status_badge: {
        variant: group.status === 'active' ? 'success' : group.status === 'inactive' ? 'warning' : 'secondary',
        text: group.status.charAt(0).toUpperCase() + group.status.slice(1)
      }
    };
  }
};

// Parent Info & Sibling Management API
export const parentsAPI = {
  // Get all parents with filters
  getAll: (params = {}) => apiClient.get('parents', { params }),
  
  // Get single parent
  getById: (id) => apiClient.get(`parents/${id}`),
  
  // Create new parent
  create: (data) => apiClient.post('parents', data),
  
  // Update parent
  update: (id, data) => apiClient.put(`parents/${id}`, data),
  
  // Delete parent
  delete: (id) => apiClient.delete(`parents/${id}`),
};

// Sibling Management API
export const siblingsAPI = {
  // Get parents grouped with their children
  getGroupedByParent: (params = {}) => apiClient.get('student-parent-mappings/grouped-by-parent', { params }),
  
  // Get statistics
  getStatistics: (params = {}) => apiClient.get('student-parent-mappings/statistics', { params }),
  
  // Assign single student to parent
  assignStudent: (data) => apiClient.post('student-parent-mappings', data),
  
  // Bulk assign students to parent
  bulkAssignStudents: (data) => apiClient.post('student-parent-mappings/bulk-assign', data),
  
  // Remove student from parent (unassign)
  unassignStudent: (mappingId) => apiClient.delete(`student-parent-mappings/${mappingId}`),
  
  // Get parents for a student
  getParentsForStudent: (studentUserId) => apiClient.get(`student-parent-mappings/student/${studentUserId}/parents`),
  
  // Get students for a parent
  getStudentsForParent: (parentUserId) => apiClient.get(`student-parent-mappings/parent/${parentUserId}/students`),
};

// Sibling helper functions
export const siblingHelpers = {
  // Validate parent data
  validateParentData: (data) => {
    const errors = [];
    
    if (!data.father_name?.trim() && !data.mother_name?.trim() && !data.guardian_name?.trim()) {
      errors.push('At least one of father name, mother name, or guardian name is required');
    }
    
    if (!data.contact_number?.trim()) {
      errors.push('Contact number is required');
    }
    
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Please enter a valid email address');
    }
    
    return errors;
  },

  // Get parent display name
  getParentDisplayName: (parent) => {
    const names = [];
    if (parent.father_name) names.push(`F: ${parent.father_name}`);
    if (parent.mother_name) names.push(`M: ${parent.mother_name}`);
    if (parent.guardian_name) names.push(`G: ${parent.guardian_name}`);
    return names.join(' | ') || 'No Name';
  },

  // Format parent data for display
  formatForDisplay: (parent) => {
    return {
      ...parent,
      display_name: siblingHelpers.getParentDisplayName(parent),
      children_count: parent.children?.length || parent.children_count || 0,
    };
  }
};

// Fee helper functions
export const feeHelpers = {
  // Format currency display
  formatCurrency: (amount, currency = '₹') => {
    return `${currency} ${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  },
  
  // Calculate balance amount
  calculateBalance: (totalAmount, paidAmount, discountAmount = 0, scholarshipAmount = 0) => {
    return parseFloat(totalAmount) - parseFloat(paidAmount) - parseFloat(discountAmount) - parseFloat(scholarshipAmount);
  },
  
  // Get payment status badge properties
  getPaymentStatusBadge: (status) => {
    const statusMap = {
      'pending': { variant: 'warning', text: 'Pending' },
      'partial': { variant: 'info', text: 'Partial' },
      'paid': { variant: 'success', text: 'Paid' },
      'overdue': { variant: 'danger', text: 'Overdue' },
      'cancelled': { variant: 'secondary', text: 'Cancelled' }
    };
    return statusMap[status] || { variant: 'secondary', text: 'Unknown' };
  },
  
  // Get term display name
  getTermDisplay: (term) => {
    const termMap = {
      'term_1': 'Term 1',
      'term_2': 'Term 2',
      'full_year': 'Full Year'
    };
    return termMap[term] || term;
  },
  
  // Validate fee structure data
  validateFeeStructure: (data) => {
    const errors = [];
    
    if (!data.structure_name?.trim()) {
      errors.push('Structure name is required');
    }
    
    if (!data.category_id) {
      errors.push('Fee category is required');
    }
    
    if (!data.class_id) {
      errors.push('Class is required');
    }
    
    if (!data.amount || parseFloat(data.amount) <= 0) {
      errors.push('Valid amount is required');
    }
    
    return errors;
  },
  
  // Format fee record for display
  formatFeeForDisplay: (fee) => {
    return {
      ...fee,
      formatted_amount: feeHelpers.formatCurrency(fee.total_amount),
      formatted_paid: feeHelpers.formatCurrency(fee.paid_amount),
      formatted_balance: feeHelpers.formatCurrency(fee.balance_amount),
      payment_status_badge: feeHelpers.getPaymentStatusBadge(fee.payment_status),
      term_display: feeHelpers.getTermDisplay(fee.term)
    };
  }
};

// Feedback Management API
export const feedbackAPI = {
  // Get all feedback with filters
  getAll: (params = {}) => apiClient.get('feedback', { params }),
  
  // Get feedback statistics
  getStats: (params = {}) => apiClient.get('feedback/stats', { params }),
  
  // Get single feedback
  getById: (id) => apiClient.get(`feedback/${id}`),
  
  // Create new feedback
  create: (data) => apiClient.post('feedback', data),
  
  // Update feedback
  update: (id, data) => apiClient.put(`feedback/${id}`, data),
  
  // Delete feedback
  delete: (id) => apiClient.delete(`feedback/${id}`),
  
  // Get replies for feedback
  getReplies: (id) => apiClient.get(`feedback/${id}/replies`),
  
  // Add reply to feedback
  addReply: (id, data) => apiClient.post(`feedback/${id}/replies`, data),
  
  // Delete reply from feedback
  deleteReply: (feedbackId, replyId) => apiClient.delete(`feedback/${feedbackId}/replies/${replyId}`),
  
  // Update feedback status (admin only)
  updateStatus: (id, status) => apiClient.put(`feedback/${id}`, { status }),
  
  // Get feedback by status
  getByStatus: (status, params = {}) => apiClient.get('feedback', { params: { ...params, status } }),
  
  // Get feedback by creator
  getByCreator: (creatorId, params = {}) => apiClient.get('feedback', { params: { ...params, created_by: creatorId } }),
  
  // Search feedback
  search: (query, params = {}) => apiClient.get('feedback', { params: { ...params, search: query } }),

  // Image management for feedback
  uploadImages: (feedbackId, formData) => apiClient.post(`feedback/${feedbackId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  getImages: (feedbackId) => apiClient.get(`feedback/${feedbackId}/images`),
  
  deleteImage: (feedbackId, imageId) => apiClient.delete(`feedback/${feedbackId}/images/${imageId}`),
  
  getImageStats: (feedbackId) => apiClient.get(`feedback/${feedbackId}/images/stats`),
  
  // Image management for replies
  uploadReplyImages: (feedbackId, replyId, formData) => apiClient.post(`feedback/${feedbackId}/replies/${replyId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  getReplyImages: (feedbackId, replyId) => apiClient.get(`feedback/${feedbackId}/replies/${replyId}/images`),
  
  // Confirmation management
  getPendingConfirmations: () => apiClient.get('feedback/confirmations/pending'),
  
  respondToConfirmation: (confirmationId, data) => apiClient.post(`feedback/confirmations/${confirmationId}/respond`, data),
  
  getConfirmationHistory: (feedbackId) => apiClient.get(`feedback/${feedbackId}/confirmations`)
};

// Feedback helper functions
export const feedbackHelpers = {
  // Get status badge configuration
  getStatusBadge: (status) => {
    const badges = {
      'created': { class: 'bg-info', text: 'Created', color: 'info' },
      'in_progress': { class: 'bg-warning', text: 'In Progress', color: 'warning' },
      'pending': { class: 'bg-warning', text: 'Pending', color: 'warning' },
      'on_hold': { class: 'bg-secondary', text: 'On Hold', color: 'secondary' },
      'closed': { class: 'bg-dark', text: 'Closed', color: 'dark' },
      'resolved': { class: 'bg-success', text: 'Resolved', color: 'success' },
      'invalid': { class: 'bg-danger', text: 'Invalid', color: 'danger' },
      're-open': { class: 'bg-primary', text: 'Re-opened', color: 'primary' },
    };
    return badges[status] || { class: 'bg-secondary', text: 'Unknown', color: 'secondary' };
  },
  
  // Check if user can reply to feedback
  canReply: (feedback, user) => {
    if (!feedback || !user) return false;
    
    // Admin can always reply, even when feedback is closed
    if (user.role_id === 1) return true;
    
    // Other users cannot reply if feedback is closed
    if (feedback.status === 'closed') return false;
    
    // Other users can reply if feedback is not closed
    if (user.role_id === 2 || user.role_id === 3 || user.role_id === 4) return true;
    
    return false;
  },
  
  // Check if user can change feedback status
  canChangeStatus: (user) => {
    return user && user.role_id === 1; // Only admin
  },
  
  // Get status options based on current status and user role
  getStatusOptions: (currentStatus = null, userRole = null) => {
    const allStatuses = [
      { value: 'in_progress', label: 'In Progress' },
      { value: 'pending', label: 'Pending' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'invalid', label: 'Invalid' },
      { value: 're-open', label: 'Re-opened' },
      { value: 'closed', label: 'Closed' },
    ];

    // Filter out current status
    let availableStatuses = allStatuses.filter(option => option.value !== currentStatus);
    
    // Business rule: closed status only available if current status is resolved
    if (currentStatus !== 'resolved') {
      availableStatuses = availableStatuses.filter(option => option.value !== 'closed');
    }
    
    // Business rule: if current status is closed, only allow re-open
    if (currentStatus === 'closed') {
      availableStatuses = availableStatuses.filter(option => option.value === 're-open');
    }
    
    return availableStatuses;
  },
  
  // Format feedback for display
  formatForDisplay: (feedback) => {
    return {
      ...feedback,
      status_badge: feedbackHelpers.getStatusBadge(feedback.status),
      formatted_created_at: feedback.created_at ? new Date(feedback.created_at).toLocaleDateString() : 'N/A',
      formatted_updated_at: feedback.updated_at ? new Date(feedback.updated_at).toLocaleDateString() : 'N/A',
      creator_display_name: feedback.created_by_details?.display_name || 'Unknown User',
      creator_role: feedback.created_by_details?.role_id || 0,
      has_replies: feedback.replies_count > 0,
      is_student_feedback: feedback.created_by_details?.role_id === 3,
      class_section_info: feedback.created_by_details?.class_section || null,
      can_reply: feedbackHelpers.canReply(feedback, feedback.user),
    };
  },
  
  // Validate feedback data
  validateFeedback: (data) => {
    const errors = [];
    
    if (!data.title || data.title.trim() === '') {
      errors.push('Feedback title is required');
    }
    
    if (!data.content || data.content.trim() === '') {
      errors.push('Feedback content is required');
    }
    
    if (data.title && data.title.length > 255) {
      errors.push('Feedback title must be less than 255 characters');
    }
    
    return errors;
  },
  
  // Validate reply data
  validateReply: (data) => {
    const errors = [];
    
    if (!data.reply_content || data.reply_content.trim() === '') {
      errors.push('Reply content is required');
    }
    
    return errors;
  }
};

// Academic Years API
export const academicAPI = {
  // Get all academic years
  getAll: (params = {}) => apiClient.get('academic-years', { params }),
  
  // Get current academic year
  getCurrent: () => apiClient.get('academic-years/current'),
  
  // Get statistics for academic years
  getStatistics: () => apiClient.get('academic-years/statistics'),
  
  // Get analytics for a specific academic year
  getAnalytics: (academicId) => apiClient.get(`academic-years/${academicId}/analytics`),
  
  // Get analytics for current academic year
  getCurrentAnalytics: async () => {
    const state = store.getState();
    const academicId = state.auth?.current_academic_id;
    
    if (!academicId) {
      throw new Error('No current academic year found in state');
    }
    
    return apiClient.get(`academic-years/${academicId}/analytics`);
  },
  
  // Create new academic year
  create: (data) => apiClient.post('academic-years', data),
  
  // Update academic year
  update: (id, data) => apiClient.put(`academic-years/${id}`, data),
  
  // Delete academic year
  delete: (id) => apiClient.delete(`academic-years/${id}`),
  
  // Set academic year as current
  setCurrent: (id) => apiClient.post(`academic-years/${id}/set-current`),
  
  // Duplicate academic year
  duplicate: (id, data) => apiClient.post(`academic-years/${id}/duplicate`, data),

  // Get structure preview for an academic year (classes → sections → subjects)
  getStructurePreview: (academicId) => apiClient.get(`academic-years/${academicId}/structure-preview`),

  // Get destination academic year summary (existing structure counts)
  getDestinationSummary: (academicId) => apiClient.get(`academic-years/${academicId}/destination-summary`),

  // Copy structure from one academic year to another
  copyStructure: (data) => apiClient.post('academic-years/copy-structure', data),
};

// Search API
export const searchAPI = {
  // Search users by role
  searchUsers: (params) => apiClient.get('search/users', { params }),

  // Search all users across every role (no role_id required)
  searchAllUsers: (params) => apiClient.get('search/all-users', { params }),
  
  // Search teachers specifically
  searchTeachers: (params) => apiClient.get('search/teachers', { params }),

  // Search non-student employees for transport driver assignment
  searchEmployees: (params) => apiClient.get('search/employees', { params }),

  // Search students specifically
  searchStudents: (params) => apiClient.get('search/students', { params }),
  
  // Search parents specifically
  searchParents: (params) => apiClient.post('search/parents', params),
  
  // Search student fees for payment recording
  searchStudentFees: (params) => apiClient.get('search/student-fees', { params }),
  
  // Search student groups
  searchStudentGroups: async (params) => {
    const response = await apiClient.get('student-groups', { 
      params: {
        search: params.search,
        ...params
      }
    });
    
    // Transform the response to match SearchDropdown format
    if (response.data.success && response.data.data) {
      const groups = Array.isArray(response.data.data) ? response.data.data : response.data.data.data || [];
      return {
        data: {
          success: true,
          data: groups.map(group => ({
            key: group.group_id,
            label: group.group_name,
            value: group.group_id,
            group_code: group.group_code,
            description: group.description
          }))
        }
      };
    }
    
    return response;
  },
  
  // Generic search endpoint
  search: (endpoint, searchTerm, params = {}) => {
    const searchParams = { 
      search: searchTerm,
      ...params 
    };
    return apiClient.get(`search/${endpoint}`, { params: searchParams });
  }
};

// Generic API utility for dynamic endpoints
export const genericAPI = {
  // Generic GET request
  get: (endpoint, params = {}) => apiClient.get(endpoint, { params }),
  
  // Generic POST request  
  post: (endpoint, data = {}) => apiClient.post(endpoint, data),
  
  // Generic PUT request
  put: (endpoint, data = {}) => apiClient.put(endpoint, data),
  
  // Generic DELETE request
  delete: (endpoint) => apiClient.delete(endpoint)
};

// Exams API
export const examsAPI = {
  // Core exam management
  getAll: (params = {}) => apiClient.get('exams', { params }),
  getById: (id) => apiClient.get(`exams/${id}`),
  create: (data) => apiClient.post('exams', data),
  update: (id, data) => apiClient.put(`exams/${id}`, data),
  delete: (id) => apiClient.delete(`exams/${id}`),
  getOptions: (params = {}) => apiClient.get('exams/options', { params }),
  getStatistics: (params = {}) => apiClient.get('exams/statistics', { params }),
  
  // Publishing controls
  publish: (id) => apiClient.patch(`exams/${id}/publish`),
  unpublish: (id) => apiClient.patch(`exams/${id}/unpublish`),
  
  // Exam Timetable Management
  timetable: {
    getAll: (params = {}) => apiClient.get('exam-timetables', { params }),
    getById: (id) => apiClient.get(`exam-timetables/${id}`),
    create: (data) => apiClient.post('exam-timetables', data),
    update: (id, data) => apiClient.put(`exam-timetables/${id}`, data),
    delete: (id) => apiClient.delete(`exam-timetables/${id}`),
    getByExamClassSection: (params = {}) => apiClient.get('exam-timetables/by-exam-class-section', { params }),
    export: (params = {}) => apiClient.get('exam-timetables/export', { params, responseType: 'blob' }),
    downloadTemplate: (params = {}) => apiClient.get('exam-timetables/download-template', { params, responseType: 'blob' }),
    import: (formData) => apiClient.post('exam-timetables/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  
  // Exam Marks Management
  marks: {
    getAll: (params = {}) => apiClient.get('exam-marks', { params }),
    getStudentMarks: (params = {}) => apiClient.get('exam-marks/students', { params }),
    getByTimetable: (timetableId, params = {}) => apiClient.get(`exam-marks/timetable/${timetableId}`, { params }),
    getExamSummary: (examId, params = {}) => apiClient.get(`exam-marks/exam/${examId}/summary`, { params }),
    update: (id, data) => apiClient.put(`exam-marks/${id}`, data),
    bulkUpdate: (data) => apiClient.patch('exam-marks/bulk-update', data),
    verify: (data) => apiClient.patch('exam-marks/verify', data),
    export: (data) => apiClient.post('exam-marks/export', data, { responseType: 'blob' }),
    import: (formData) => apiClient.post('exam-marks/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    generateReportCards: (examId, data) => apiClient.post(`exam-marks/exam/${examId}/generate-report-cards`, data)
  },
  
  // Report Cards Management
  reportCards: {
    getAll: (params = {}) => apiClient.get('exam-report-cards', { params }),
    getById: (id) => apiClient.get(`exam-report-cards/${id}`),
    update: (id, data) => apiClient.put(`exam-report-cards/${id}`, data),
    getByClassSection: (params = {}) => apiClient.get('exam-report-cards/by-class-section', { params }),
    getExamSummary: (examId, params = {}) => apiClient.get(`exam-report-cards/exam/${examId}/summary`, { params }),
    publish: (data) => apiClient.patch('exam-report-cards/publish', data),
    unpublish: (data) => apiClient.patch('exam-report-cards/unpublish', data),
    generatePDF: (id) => apiClient.get(`exam-report-cards/${id}/pdf`, { responseType: 'blob' }),
    generateBulkPDF: (data) => apiClient.post('exam-report-cards/bulk-pdf', data, { responseType: 'blob' }),
    generateStudentReportPDF: (examId, studentId) => apiClient.get(`exam-report-cards/exam/${examId}/student/${studentId}/pdf`, { responseType: 'blob' }),
    export: (data) => apiClient.post('exam-report-cards/export', data, { responseType: 'blob' }),
    regenerate: (id, data) => apiClient.patch(`exam-report-cards/${id}/regenerate`, data)
  },
  
  // System utilities
  system: {
    getDashboard: (params = {}) => apiClient.get('exam-system/dashboard', { params }),
    bulkDeleteExams: (data) => apiClient.post('exam-system/bulk-delete-exams', data)
  },
  
  // Public routes for parents/students
  public: {
    getStudentReportCard: (studentId, examId) => apiClient.get(`exam-results/student/${studentId}/exam/${examId}`),
    downloadStudentReportCard: (studentId, examId) => apiClient.get(`exam-results/student/${studentId}/exam/${examId}/pdf`, { responseType: 'blob' })
  }
};

export default apiClient;

// Legacy API export for backward compatibility
export const api = apiClient;

// Subscription API
export const subscriptionAPI = {
  getStatus: () => apiClient.get('subscription/status'),
  getPlans: () => apiClient.get('subscription/plans'),
  initiatePayment: () => apiClient.post('subscription/initiate-payment'),
  requestPlanChange: (data) => apiClient.post('subscription/request-plan-change', data),
};

// Online Exam Management APIs
export const onlineExamsAPI = {
  getAll: (params = {}) => apiClient.get('online-exams', { params }),
  getById: (id) => apiClient.get(`online-exams/${id}`),
  create: (data) => apiClient.post('online-exams', data),
  update: (id, data) => apiClient.put(`online-exams/${id}`, data),
  delete: (id) => apiClient.delete(`online-exams/${id}`),
  publish: (id) => apiClient.patch(`online-exams/${id}/publish`),
  unpublish: (id) => apiClient.patch(`online-exams/${id}/unpublish`),
  duplicate: (id) => apiClient.post(`online-exams/${id}/duplicate`),
  getStudentExams: (params = {}) => apiClient.get('online-exams/student', { params }),
  getExamDetails: (id) => apiClient.get(`online-exams/${id}/details`)
};

export const onlineExamQuestionsAPI = {
  getAll: (params = {}) => apiClient.get('online-exam-questions', { params }),
  getById: (id) => apiClient.get(`online-exam-questions/${id}`),
  getByExam: (examId, params = {}) => apiClient.get(`online-exams/${examId}/questions`, { params }),
  create: (data) => apiClient.post('online-exam-questions', data),
  update: (id, data) => apiClient.put(`online-exam-questions/${id}`, data),
  delete: (id) => apiClient.delete(`online-exam-questions/${id}`),
  reorder: (examId, data) => apiClient.patch(`online-exams/${examId}/questions/reorder`, data),
  addFromBank: (examId, data) => apiClient.post(`online-exams/${examId}/questions/add-from-bank`, data),
  bulkImport: (formData) => {
    // Extract examId from formData if available, or use nested route
    const examId = formData.get('online_exam_id');
    if (examId) {
      return apiClient.post(`online-exams/${examId}/exam-questions/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    // Fallback to direct route
    return apiClient.post('online-exam-questions/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  export: (examId) => apiClient.get(`online-exams/${examId}/questions/export`, { responseType: 'blob' }),
  downloadTemplate: () => apiClient.get('online-exams/question-templates/download', { responseType: 'blob' }),
  getTemplateUrl: () => apiClient.get('online-exams/question-templates/url')
};

export const onlineExamAttemptsAPI = {
  getAll: (params = {}) => apiClient.get('online-exam-attempts', { params }),
  getById: (id) => apiClient.get(`online-exam-attempts/${id}`),
  getByExam: (examId, params = {}) => apiClient.get(`online-exams/${examId}/attempts`, { params }),
  getByStudent: (studentId, params = {}) => apiClient.get(`students/${studentId}/exam-attempts`, { params }),
  create: (data) => apiClient.post('online-exam-attempts', data),
  update: (id, data) => apiClient.put(`online-exam-attempts/${id}`, data),
  submit: (id, data) => apiClient.patch(`online-exam-attempts/${id}/submit`, data),
  regrade: (id) => apiClient.patch(`online-exam-attempts/${id}/regrade`),
  regradeAll: (examId) => apiClient.patch(`online-exams/${examId}/attempts/regrade-all`),
  export: (examId, params = {}) => apiClient.get(`online-exams/${examId}/attempts/export`, { 
    params, 
    responseType: 'blob' 
  }),
  getDetailedResults: (id) => apiClient.get(`online-exam-attempts/${id}/detailed-results`)
};

export const onlineExamAnswersAPI = {
  getAll: (params = {}) => apiClient.get('online-exam-answers', { params }),
  getByAttempt: (attemptId, params = {}) => apiClient.get(`online-exam-attempts/${attemptId}/answers`, { params }),
  saveAnswer: (data) => apiClient.post('online-exam-answers', data),
  updateAnswer: (id, data) => apiClient.put(`online-exam-answers/${id}`, data),
  bulkSave: (data) => apiClient.post('online-exam-answers/bulk-save', data)
};

export const questionBankAPI = {
  getAll: (params = {}) => apiClient.get('question-bank', { params }),
  getById: (id) => apiClient.get(`question-bank/${id}`),
  create: (data) => apiClient.post('question-bank', data),
  update: (id, data) => apiClient.put(`question-bank/${id}`, data),
  delete: (id) => apiClient.delete(`question-bank/${id}`),
  bulkImport: (formData) => apiClient.post('question-bank/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  export: (params = {}) => apiClient.get('question-bank/export', { params, responseType: 'blob' }),
  getCategories: () => apiClient.get('question-bank/categories'),
  duplicate: (id) => apiClient.post(`question-bank/${id}/duplicate`)
};

export const transferCertificateAPI = {
  list: (params = {}) => apiClient.get('transfer-certificates', { params }),
  issue: (data) => apiClient.post('transfer-certificates/generate', data, { responseType: 'blob' }),
  show: (id) => apiClient.get(`transfer-certificates/${id}`),
  void: (id, reason) => apiClient.delete(`transfer-certificates/${id}`, { data: { reason } }),
  previewPdf: (studentId, data = {}) => apiClient.get(`students/${studentId}/transfer-certificate/preview`, { params: data, responseType: 'blob' }),
  downloadPdf: (id) => apiClient.get(`transfer-certificates/${id}/pdf`, { responseType: 'blob' }),
  tcLookup: (studentId) => apiClient.get(`students/${studentId}/tc-lookup`)
};
